// src/components/Subscription/LockedFeature.jsx
//
// "Show but Lock" wrapper. If the user's plan unlocks the feature, children
// render transparently. Otherwise children remain visible (so the user can
// SEE the feature) but interaction is intercepted and routes to a single
// global upgrade modal.
//
// Props:
//   feature       — key in SubscriptionContext.featureMap (required)
//   preview       — 'badge' | 'blur' | 'overlay' | 'inline' (default 'badge')
//   promoText     — short string shown in the upgrade modal title
//   ctaPlan       — 'standard' | 'premium' | 'family' | 'business' (required)
//   children      — the underlying paid UI (always rendered)
//   disabled      — when true and preview='inline', also disables child chips
//
import React, { cloneElement, isValidElement } from 'react';
import { Box, Button, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';

import { useSubscription } from '../../contexts/SubscriptionContext';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import PremiumBadge from './PremiumBadge';

const LockedFeature = ({
  feature,
  preview = 'badge',
  promoText,
  ctaPlan = 'premium',
  disabled = false,
  children,
  sx = {},
}) => {
  const { t } = useTranslation();
  const { hasFeature, subscription } = useSubscription();
  const { open } = useUpgradeModal();

  const unlocked = feature ? hasFeature(feature) : false;

  // Unlocked → render transparently, no overlay, no badge.
  if (unlocked) {
    return <>{children}</>;
  }

  const triggerModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    open({
      feature,
      requiredPlan: ctaPlan,
      promoText: promoText || feature || '',
      currentPlan: subscription?.plan || 'free',
      source: 'user',
    });
  };

  // ── badge: render children + small absolutely-positioned tier chip ────────
  if (preview === 'badge') {
    return (
      <Box sx={{ position: 'relative', display: 'inline-block', ...sx }}>
        {children}
        <Box
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            zIndex: 2,
            cursor: 'pointer',
          }}
          onClick={triggerModal}
        >
          <PremiumBadge tier={ctaPlan} size="small" />
        </Box>
      </Box>
    );
  }

  // ── blur: blurred children + centered lock icon, click → modal ────────────
  if (preview === 'blur') {
    return (
      <Box
        onClick={triggerModal}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          borderRadius: 2,
          overflow: 'hidden',
          ...sx,
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') triggerModal(e);
        }}
        aria-label={t('subscription.locked.lockedFeature', 'This is a paid feature')}
      >
        <Box
          sx={{
            filter: 'blur(4px)',
            pointerEvents: 'none',
            userSelect: 'none',
            transition: 'filter 0.2s',
          }}
        >
          {children}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.25) 100%)',
          }}
        >
          <LockIcon sx={{ fontSize: 32, color: 'common.white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }} />
          <PremiumBadge tier={ctaPlan} size="small" />
        </Box>
      </Box>
    );
  }

  // ── overlay: dark gradient over children + CTA button ─────────────────────
  if (preview === 'overlay') {
    return (
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', ...sx }}>
        <Box sx={{ pointerEvents: 'none', userSelect: 'none' }}>{children}</Box>
        <Box
          onClick={triggerModal}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            cursor: 'pointer',
            background:
              'linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.85) 100%)',
            backdropFilter: 'blur(2px)',
            transition: 'background 0.2s',
            '&:hover': {
              background:
                'linear-gradient(180deg, rgba(15,23,42,0.6) 0%, rgba(15,23,42,0.9) 100%)',
            },
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') triggerModal(e);
          }}
        >
          <LockIcon sx={{ fontSize: 28, color: 'common.white' }} />
          {promoText && (
            <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 600, textAlign: 'center', px: 2 }}>
              {promoText}
            </Typography>
          )}
          <Button
            variant="contained"
            color={ctaPlan === 'standard' ? 'info' : 'warning'}
            startIcon={<LockIcon />}
            onClick={triggerModal}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            {t('subscription.locked.unlockWith', 'Unlock with {{plan}}', {
              plan: t(`subscription.locked.${ctaPlan}`, ctaPlan.toUpperCase()),
            })}
          </Button>
        </Box>
      </Box>
    );
  }

  // ── inline: 🔒 prefix + reduced opacity, click → modal ────────────────────
  // If children is a single element and `disabled` is true, also pass through
  // the disabled prop (handy for chips / buttons).
  if (preview === 'inline') {
    let rendered = children;
    if (disabled && isValidElement(children)) {
      try {
        rendered = cloneElement(children, { disabled: true });
      } catch (_) {
        rendered = children;
      }
    }
    return (
      <Box
        onClick={triggerModal}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          opacity: 0.6,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.85 },
          ...sx,
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') triggerModal(e);
        }}
        aria-label={t('subscription.locked.lockedFeature', 'This is a paid feature')}
      >
        <Box component="span" sx={{ fontSize: '0.85em', lineHeight: 1 }}>
          🔒
        </Box>
        <Box sx={{ pointerEvents: 'none' }}>{rendered}</Box>
      </Box>
    );
  }

  // Unknown preview value — fall through to plain children.
  return <>{children}</>;
};

export default LockedFeature;
